import React from "react";

import { isAdminType, MODULE_ROLES, UserType } from "../../auth/authApi";
import { USER_TYPE_LABELS } from "./UserTypeBadge";

/** O que cada acesso adicional libera — o rótulo sozinho não diz. */
const DESCRICOES: Partial<Record<UserType, string>> = {
  DP: "Departamento Pessoal completo, inclusive dado de saúde dos afastamentos.",
  FINANCEIRO: "Financeiro completo: lança, baixa, edita e configura.",
  FINANCEIRO_LEITURA: "Financeiro só para consulta.",
  GESTOR_CAMPANHAS: "Campanhas na Meta e candidatos (criar, publicar, acompanhar).",
};

/**
 * Lista que o backend aceita para o tipo principal (ADR 0024): ADMIN não tem
 * adicional, o principal não se repete e a leitura sai quando há escrita.
 * Usada ao trocar o tipo principal no formulário.
 */
export function sanitizeExtraRoles(principal: UserType, extraRoles: UserType[]): UserType[] {
  if (isAdminType(principal)) return [];
  const roles = extraRoles.filter((r) => r !== principal && MODULE_ROLES.includes(r));
  const hasWrite = roles.includes("FINANCEIRO") || principal === "FINANCEIRO";
  return hasWrite ? roles.filter((r) => r !== "FINANCEIRO_LEITURA") : roles;
}

type Props = {
  principal: UserType;
  value: UserType[];
  onChange: (next: UserType[]) => void;
  disabled?: boolean;
  /** Papéis que quem edita pode conceder; os demais não aparecem (administrador restrito, ADR 0027). */
  canGrant?: (role: UserType) => boolean;
};

/** Acessos adicionais ao tipo principal, nos modais de criar e editar usuário. */
const ExtraRolesPicker: React.FC<Props> = ({ principal, value, onChange, disabled, canGrant }) => {
  if (isAdminType(principal)) {
    return (
      <div className="admFormGroup">
        <label>Acessos adicionais</label>
        <p className="admMuted" style={{ margin: 0 }}>
          Administrador já alcança todos os módulos — não recebe acessos adicionais.
        </p>
      </div>
    );
  }

  const options = MODULE_ROLES.filter((r) => r !== principal && (!canGrant || canGrant(r)));
  if (!options.length) return null;
  const hasWrite = value.includes("FINANCEIRO") || principal === "FINANCEIRO";

  const toggle = (role: UserType) => {
    const next = value.includes(role) ? value.filter((r) => r !== role) : [...value, role];
    onChange(sanitizeExtraRoles(principal, next));
  };

  return (
    <div className="admFormGroup">
      <label>Acessos adicionais</label>
      <div className="admStorePicker__list">
        {options.map((role) => {
          const readCoveredByWrite = role === "FINANCEIRO_LEITURA" && hasWrite;
          return (
            // O `.admFormGroup label` deixaria a descrição em caixa alta.
            <label
              key={role}
              className="admStoreOption"
              style={{ textTransform: "none", letterSpacing: "normal", fontWeight: 400 }}
            >
              <input
                type="checkbox"
                checked={value.includes(role) || readCoveredByWrite}
                disabled={disabled || readCoveredByWrite}
                onChange={() => toggle(role)}
              />
              <span className="admStoreOption__name">
                <strong>{USER_TYPE_LABELS[role]}</strong> — {DESCRICOES[role]}
              </span>
            </label>
          );
        })}
      </div>
      <span className="admFormHint">
        Somam-se ao tipo principal, que continua decidindo a tela inicial e as lojas vinculadas.
      </span>
    </div>
  );
};

export default ExtraRolesPicker;
